import { Content } from "./content";
import { Conversation } from "./conversation";
import { Origin } from "./origin";
import { Parameter } from "./parameter";
import { v4 as uuidv4 } from 'uuid';
import { TextContent } from "./text-content";

/**
 * Definição de uma Mensagem que pode transitar tanto da UI
 * para o Serviço, como do Serviço para a UI.
 *
 * Trata-se de uma classe abstrata e não pode ser usada diretamente.
 * As duas implementações mais comuns de uma mensagem são Question e Answer.
 *
 */
export class Message {

    // Identificador no formato UUID, único.
    // É gerado automaticamente, não precisa ser informado.
    id: string;

    // Qual conversação a mensagem pertence.
    conversation!: Conversation;

    // Lista de parâmetros obtidos pela UI através da QueryString.
    parameters: Parameter[];

    // Conteúdo da mensagem.
    // Uma mensagem pode conter mais de um conteúdo, de diversos tipos.
    contents: Content[];

    // Origem da mensagem: Bot ou Usuário?
    origin: Origin;

    // Data que a mensagem foi criada.
    date: Date;

    // Lista de parâmetros de configuração da UI enviadas pelo Serprobots.
    ui: Parameter[] = [];

    // Informa se é uma mensagem sem conteúdo.
    empty: boolean = false;

    // Questão que deu origem a esta mensagem.
    question: string;

    // Informa se essa mensagem contém dados sobre um erro. Isto pode afetar a forma como a mensagem é exibida.
    isError: boolean = false;

    constructor(origin: Origin = Origin.BOT, contents: Content[] = [], parameters: Parameter[] = [], date: Date = new Date(), question: string = "", empty: boolean = false) {
        this.id = uuidv4();
        this.parameters = parameters;
        this.contents = contents;
        this.origin = origin;
        this.date = date;
        this.question = question;
        this.empty = empty;
    }

    /**
     * Cria uma Mensagem a partir de um texto.
     *
     * Será retornada uma mensagem com um único conteúdo do tipo TextContent,
     * cujo texto será igual ao parâmetro.
     *
     * @param text Texto
     */
    public static of(text: string): Message {
        const message = new Message();
        const textContent = new TextContent(text);

        message.addContent(textContent);

        return message;
    }

    addContent(content: Content) {
        this.contents.push(content);
    }

    /**
     * Informa se a lista de opções deve ser desabilitada após o usuário selecionar uma opção.
     * Esta informação encontra-se na lista de parâmetros da UI dentro de
     * um parâmetro nomeado "disableOptionsAfterSelection".
     *
     * @returns True caso seja para desabilitar, False caso contrário.
     */
    shouldDisableOptionsAfterSelection(): boolean {
        var result = false;

        for (let param of this.ui) {
            if (param.name == "disableOptionsAfterSelection") {
                result = (param.value === "true" || param.value === "True" || param.value as any === true);
                break;
            }
        }

        return result;
    }

    /**
     * Informa se deve aguardar ou não a entrada do usuário.
     *
     * @returns Caso seja False, aguarda que o usuário digite uma mensagem. Caso seja True, envia uma mensagem vazia para o gateway imediatamente.
     */
    skipUserInput(): boolean {
        let result = false;

        for (let param of this.ui) {
            if (param.name == "skipUserInput") {
                result = (param.value === "true" || param.value === "True" || param.value as any  === true);
                break;
            }
        }

        return result;
    }

    usePasswordInput(): boolean {
      let result = false;
      for (let param of this.ui) {
          if (param.name == "usePasswordInput") {
              result = (param.value === "true" || param.value === "True" || param.value as any  === true);
              break;
          }
      }

      return result;
    }


    /**
     * Adicionar um novo parâmetro para a UI.
     * Não adiciona caso já exista um parâmetro com o mesmo nome.
     *
     * @param parameter Parâmetro que deve ser adicionado.
     */
    addUIParameter(parameter: Parameter) {
        let index = this.ui.findIndex((p) => p.name == parameter.name);

        if (index < 0) {
            this.ui.push(parameter);
        }
    }

}
